import { Component, effect, inject } from '@angular/core';
import { UserStateService } from '../../../common/store/state/user/user.state.service';
import { BaseComponent } from '../../../common/components/base.component';
import { UserDetails, UserWelcomeResponse } from '../../../common/models/Login';
import { ActivatedRoute } from '@angular/router';
import { catchError, switchMap, tap } from 'rxjs';
import { OrderService } from '../../../common/services/order/order.service';
import { SpinnerService } from '../../../common/services/spinner.service';
import { OrderStateServiceV2 } from '../../../common/store/state/order/orderv2/order-state.service';
import { OrderApiResponseStateService } from '../../../common/store/state/order/orderv2/order-api-response/order-api-response-state.service';
import { DeviceService } from '../../../common/services/device.service';
import { LocationBypassService } from '../../../common/services/location-bypass.service';
import { NotificationService } from '../../../common/services/notification.service';
import { Message } from '../../../common/enums/message.enum';
import { LoginFlowStateService } from '../../../common/store/state/login-flow/login-flow.state.service';
import { AuthService } from '../../../common/services/auth.service';

@Component({
  selector: 'app-welcome',
  templateUrl: './welcome.component.html',
  styleUrl: './welcome.component.scss',
  standalone: false
})
export class WelcomeComponent extends BaseComponent {
  orderEntryButtonEnabled = false;
  #userStateService = inject(UserStateService)
  #orderService = inject(OrderService)
  #spinnerService = inject(SpinnerService)
  #activatedRoute = inject(ActivatedRoute)
  #orderStateService = inject(OrderStateServiceV2)
  #apiResponseStateService = inject(OrderApiResponseStateService)
  #deviceService = inject(DeviceService)
  #locationBypassService = inject(LocationBypassService)
  #notificationService = inject(NotificationService)
  #loginFlowStateService = inject(LoginFlowStateService)
  #authService = inject(AuthService)

  welcomeInfo: UserWelcomeResponse;
  user: UserDetails;
  greeting = 'Welcome';

  constructor() {
    super();
    // trigger resolver to reset state if exists
    this.subscriptions.add(this.#activatedRoute.data.pipe(switchMap(data => data.welcome)).subscribe())
    effect(() => {
      this.welcomeInfo = this.#userStateService.getUserWelcomeInfo();
      this.user = this.#userStateService.getUser();
      this.greeting = this.#buildGreeting(this.user);

      if (this.welcomeInfo) {
        if (this.welcomeInfo.Modules && this.welcomeInfo.Modules.find(x => x.toLocaleLowerCase() == 'orderentry')) {
          this.orderEntryButtonEnabled = true;
        }
      }
    })
  }

  #buildGreeting(user: UserDetails): string {
    const name = [user?.FirstName, user?.LastName]
      .map(part => part?.trim())
      .filter(Boolean)
      .join(' ');
    return name ? `Hi, ${name}` : 'Welcome';
  }

  async onStartNewOrderClicked() {
    this.#spinnerService.setLoading(true, 'Loading order details...');

    try {
      await this.#initDevice();
    } catch (error) {
      console.error('Error initializing device location', error);
      const unavailable = this.#deviceService.locationDisabled() && !this.#deviceService.locationDenied();
      this.#notificationService.showError(unavailable ? Message.ErrorMessageLocationUnavailable : Message.ErrorMessageLocationAccess);
      this.#spinnerService.setLoading(false, 'Loading order details...');
      return;
    }

    this.#orderStateService.clearAll();
    this.#apiResponseStateService.clear();
    // The user's feature toggles are read again right before every order (a failure keeps the current values).
    this.subscriptions.add(this.#authService.loadFeatureToggles().pipe(
      switchMap(() => this.#orderService.getPreOrder()),
      tap(resp => this.#orderStateService.initOrder(resp.OrderNumber)),
      switchMap(() => this.#orderService.getStartOrder()),
      catchError((error) => {
        console.error('Error fetching start order data', error);
        throw error;
      })
    ).subscribe({
      next: () => {
        this.#spinnerService.setLoading(false, 'Loading order details...');
        this.router.navigate([this.orderRoutes.ORDER_SETUP], { state: { skipUrlValidation: true } });
      },
      error: () => {
        this.#spinnerService.setLoading(false, 'Loading order details...');
      }
    }))
  }

  async #initDevice(): Promise<void> {
    const skipLocationRequest = this.#locationBypassService.shouldBypassLocation(this.user?.LoginEmail)
      || !!this.#loginFlowStateService.getLoginFlow()?.LocationServicesOptional;
    await this.#deviceService.initDevice(skipLocationRequest);

    if (!skipLocationRequest && this.#deviceService.locationDisabled()) {
      throw new Error('Location access is required to start a new order.');
    }
  }
}
